<template>
  <div
    v-if="!subscriptionIsFreeTrial && $store.state.idea.storyMode.common.completedSteps.marketingBudgetCompleted === true && isMarketingBudgetVisible"
    class="journal-content dynamic-height"
    :class="{'dynamic-height': !isOption(marketingBudgetData, 'breakdownSection') && !isOption(marketingBudgetData, 'timelineSection') && !isOption(marketingBudgetData, 'tableSection')}"
  >
    <div class="content-menus">
      <div v-if="moreVisible === true" class="view-type">
        <template v-if="marketingEl.length !== 0">
          <a
            class="orange"
            :class="{'orange-off': !isOption(marketingBudgetData, 'breakdownSection')}"
            @click="enableBreakdown"
          >
            {{ $t('pages.businessPlan.sections.breakdown') }}
          </a>
          <a
            class="orange"
            :class="{'orange-off': !isOption(marketingBudgetData, 'timelineSection')}"
            @click="enableTimeline"
          >
            {{ $t('pages.businessPlan.sections.timeline') }}
          </a>
          <a
            class="orange"
            :class="{'orange-off': !isOption(marketingBudgetData, 'tableSection')}"
            @click="enableTable"
          >
            {{ $t('pages.businessPlan.sections.table') }}
          </a>
        </template>
        <p v-if="marketingEl.length === 0">
          {{ $t('pages.businessPlan.sections.youDontHaveAnyDataOnThisPage') }}
        </p>
      </div>
      <div v-if="canEdit" class="mini-actions">
        <toggle-page-items-button
          :visible="moreVisible"
          @click="toggleMore"
        />
        <toggle-view-button
          :disabled="!cardExist"
          :horizontal="!isOption(marketingBudgetData, 'gridListView')"
          @click="toggleRotate"
        />
        <toggle-notes-button
          :disabled="!moreDetails"
          :visible="isOption(marketingBudgetData, 'moreDetailsDisplay')"
          @click="enableMoreDetails"
        />
        <edit-section-link route-name="business-guide-projections-marketing-budget" />
      </div>
    </div>

    <div class="sub chapter">
      <header>
        <!-- Title section -->
        <editable-title
          v-model="marketingBudgetData.title"
          tag="h1"
          :disabled="!canEdit || shareMode"
          @save="onSave"
        />
        <!-- Title section -->
        <hr>
        <!-- Description section -->
        <editable-description
          v-model="marketingBudgetData.subTitle"
          :empty-text="$t('addMoreDetails.addNoteText')"
          :disabled="!canEdit || shareMode"
          @save="onSave"
        />
        <!-- Description section -->
      </header>

      <template v-if="marketingEl.length !== 0">
        <!-- Marketing Budget Breakdown -->
        <div v-if="isOption(marketingBudgetData, 'breakdownSection')" class="switcher-inner-wrapper breakdown-chart">
          <marketing-budget-breakdown
            :update-chart="updateCharts"
            :chart-adjustment="chartAdjustment"
          />
        </div>
        <!-- /Marketing Budget Breakdown -->

        <!-- Marketing Budget Timeline -->
        <div v-if="isOption(marketingBudgetData, 'timelineSection')" class="timeline-chart">
          <marketing-budget-timeline
            :update-chart="updateCharts"
            :chart-adjustment="chartAdjustment"
          />
        </div>
        <!-- /Marketing Budget Timeline -->

        <!-- Marketing Budget Table -->
        <div v-if="isOption(marketingBudgetData, 'tableSection')" class="table-chart">
          <marketing-budget-table
            :update-chart="updateCharts"
            :table-adjustment="tableAdjustment"
          />
        </div>
        <!-- /Marketing Budget Table -->
      </template>

      <!-- More Details section -->
      <template v-if="moreDetails && isOption(marketingBudgetData, 'moreDetailsDisplay')">
        <div class="details">
          <editable-title
            v-model="marketingBudgetData.moreDetailsTitle"
            class="bold"
            tag="p"
            :disabled="!canEdit"
            @save="onSave"
          />
          <p v-html="moreDetails" />
        </div>
      </template>
      <!-- More Details section -->
    </div>
  </div>
</template>

<script>
import EditSectionLink from '@/views/Home/StoryMode/Components/BusinessPlan/EditSectionLink'
import EditableDescription from '@/components/_v2/EditableDescription'
import EditableTitle from '@/components/_v2/EditableTitle'
import MarketingBudgetBreakdown from '@/views/Home/StoryMode/Forecast/MarketingBudget/Charts/MarketingBudgetBreakdown'
import MarketingBudgetTable from '@/views/Home/StoryMode/Forecast/MarketingBudget/Tables/MarketingBudgetTable'
import MarketingBudgetTimeline from '@/views/Home/StoryMode/Forecast/MarketingBudget/Charts/MarketingBudgetTimeline'
import MixinIdeaRoles from '@/mixins/permissions/ideaRoles'
import MixinUserSubscription from '@/mixins/permissions/userSubscription'
import ToggleNotesButton from '@/views/Home/StoryMode/Components/BusinessPlan/ToggleNotesButton'
import TogglePageItemsButton from '@/views/Home/StoryMode/Components/BusinessPlan/TogglePageItemsButton'
import ToggleViewButton from '@/views/Home/StoryMode/Components/BusinessPlan/ToggleViewButton'
import journalMethods from '@/mixins/journalMethods'

export default {
  name: 'MarketingBudget',

  components: {
    EditSectionLink,
    EditableDescription,
    EditableTitle,
    MarketingBudgetBreakdown,
    MarketingBudgetTable,
    MarketingBudgetTimeline,
    ToggleNotesButton,
    TogglePageItemsButton,
    ToggleViewButton
  },

  mixins: [
    journalMethods,
    MixinIdeaRoles,
    MixinUserSubscription
  ],

  props: {
    allPagesTextData: {
      type: Object,
      default: () => ({})
    },
    shareMode: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      cardExist: false,
      marketingBudgetData: {},
      breakdownSection: true,
      timelineSection: true,
      tableSection: true,

      moreVisible: false,
      updateCharts: true,

      moreDetails: this.$store.state.idea.storyMode.common.moreDetails.marketingBudgetMoreDetails,
      marketingEl: this.$store.state.idea.storyMode.forecast.marketingBudget.marketingElements,

      chartAdjustment: {
        chartHeight: 260,
        headingTitleFontSize: '18px',
        xLabelTitleFontSize: '10px',
        yLabelTitleFontSize: '12px',
        yaxisLabelsMinWidth: {
          breakDown: 0,
          timeLine: 0
        }
      },
      tableAdjustment: {
        journalPage: true,
        period: 'yearly',
        tableFirstColumnWidth: 250,
        columnWidth: 118
      },
      switchSelector: 'payroll'
    }
  },

  computed: {
    isMarketingBudgetVisible () {
      const index = this.$store.state.idea.journal.journalSwitchBars.indexOf(
        this.$store.state.idea.journal.journalSwitchBars.find(journalElement => journalElement.sectionName === 'MarketingBudget')
      )

      return !this.$store.state.idea.journal.journalSwitchBars[index].options.includes('switch')
    },

    isTitleEnabled () {
      return this.marketingBudgetData.title
    },

    isMoreDetailsTitleEnabled () {
      return this.marketingBudgetData.moreDetailsTitle
    }
  },

  watch: {
    allPagesTextData () {
      this.marketingBudgetData = Object.assign({}, this.allPagesTextData)
    }
  },

  mounted () {
    this.marketingBudgetData = Object.assign({}, this.allPagesTextData)
  },

  methods: {
    toggleRotate () {
      if (this.cardExist) {
        this.toggleOption(this.marketingBudgetData, 'gridListView')
        this.marketingBudgetData = Object.assign({}, this.allPagesTextData)
        this.onSave()
      }
    },

    enableMoreDetails () {
      if (this.moreDetails) {
        this.toggleOption(this.marketingBudgetData, 'moreDetailsDisplay')
        this.marketingBudgetData = Object.assign({}, this.allPagesTextData)
        this.onSave()
      }
    },

    onSave () {
      this.$emit('save', this.marketingBudgetData)
    },

    enableTimeline () {
      this.toggleOption(this.marketingBudgetData, 'timelineSection')
      this.marketingBudgetData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableBreakdown () {
      this.toggleOption(this.marketingBudgetData, 'breakdownSection')
      this.marketingBudgetData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    },

    enableTable () {
      this.toggleOption(this.marketingBudgetData, 'tableSection')
      this.marketingBudgetData = Object.assign({}, this.allPagesTextData)
      this.onSave()
    }
  }
}
</script>
